import { createContext, ReactNode, RefObject, useContext, useMemo } from 'react'

import {
  CENTER_PANEL_LARGE_MIN,
  CENTER_PANEL_MEDIUM_MAX,
  CENTER_PANEL_MEDIUM_MIN,
  CENTER_PANEL_SMALL_MAX,
} from 'features/app_shell/centerPanelBreakpoints'
import { useContainerBreakpoints } from 'hooks/use_container_breakpoints'
import { useResizeObserver } from 'hooks/use_resize_observer'

const DEFAULT_BREAKPOINTS = {
  isSmallCenterPanel: { maxWidth: CENTER_PANEL_SMALL_MAX },
  isMediumCenterPanel: { minWidth: CENTER_PANEL_MEDIUM_MIN, maxWidth: CENTER_PANEL_MEDIUM_MAX },
  isMediumDownCenterPanel: { maxWidth: CENTER_PANEL_MEDIUM_MAX },
  isMediumUpCenterPanel: { minWidth: CENTER_PANEL_MEDIUM_MIN },
  isLargeCenterPanel: { minWidth: CENTER_PANEL_LARGE_MIN },
} as const

interface CenterPanelSizeContextValue {
  width: number | undefined
  isSmallCenterPanel: boolean
  isMediumCenterPanel: boolean
  isMediumDownCenterPanel: boolean
  isMediumUpCenterPanel: boolean
  isLargeCenterPanel: boolean
}

const CenterPanelSizeContext = createContext<CenterPanelSizeContextValue | null>(null)

interface CenterPanelSizeProviderProps {
  children: (ref: RefObject<HTMLElement | null>) => ReactNode
}

/**
 * Measures the center panel's width via ResizeObserver and provides
 * default breakpoint booleans to the subtree.
 *
 * Uses a render-prop for `children` so the consumer can attach the
 * observer ref to the correct DOM element (`<main>#shell-content`).
 */
export const CenterPanelSizeProvider = ({ children }: CenterPanelSizeProviderProps) => {
  const { ref, width } = useResizeObserver<HTMLElement>()
  const breakpoints = useContainerBreakpoints(width, DEFAULT_BREAKPOINTS)

  const value = useMemo<CenterPanelSizeContextValue>(
    () => ({
      width,
      ...breakpoints,
      ...(width === undefined && { isMediumUpCenterPanel: true, isLargeCenterPanel: true }),
    }),
    [width, breakpoints],
  )

  return (
    <CenterPanelSizeContext.Provider value={value}>
      {children(ref)}
    </CenterPanelSizeContext.Provider>
  )
}

/**
 * Returns the center panel's measured width and default breakpoint booleans.
 * For custom breakpoints, use `width` with `useContainerBreakpoints` directly.
 *
 * Prefer CSS container queries (`centerPanelSmall`, `centerPanelMedium`, `centerPanelLarge`)
 * for styling. Use this hook only when JavaScript logic requires the size.
 *
 * @throws When called outside `CenterPanelSizeProvider`
 */
const DESKTOP_DEFAULT: CenterPanelSizeContextValue = {
  width: undefined,
  isSmallCenterPanel: false,
  isMediumCenterPanel: false,
  isMediumDownCenterPanel: false,
  isMediumUpCenterPanel: true,
  isLargeCenterPanel: true,
}

export const useCenterPanelSize = (): CenterPanelSizeContextValue => {
  const ctx = useContext(CenterPanelSizeContext)
  return ctx ?? DESKTOP_DEFAULT
}
